import React from 'react';
import { CalendarDays, CheckCircle2, Link2, Sparkles, Target, TrendingUp } from 'lucide-react';
import { getPromptCards } from './activationState';
import './ActivationPanel.css';
import InlineAlert from '../common/InlineAlert';

const PROMPT_ICONS = {
  target: Target,
  trend: TrendingUp,
  calendar: CalendarDays,
};

export default function ActivationPanel({
  step,
  hasPlaidConnection,
  isConnecting = false,
  error = null,
  connectControl = null,
  onConnectPlaid,
  onSkipPlaid,
  onPromptSelected,
}) {
  const promptCards = getPromptCards({ hasPlaidConnection });

  return (
    <section className="activation-panel" aria-label="First setup steps">
      <div className="activation-progress" aria-label="Activation progress">
        <span className="activation-progress-item complete">
          <CheckCircle2 size={16} aria-hidden="true" />
          Profile
        </span>
        <span className={`activation-progress-item${step !== 'connect' ? ' complete' : ' active'}`}>
          <Link2 size={16} aria-hidden="true" />
          Connect
        </span>
        <span className={`activation-progress-item${step === 'prompts' ? ' active' : ''}`}>
          <Sparkles size={16} aria-hidden="true" />
          Ask
        </span>
      </div>

      {step === 'connect' ? (
        <div className="activation-section">
          <div className="activation-copy">
            <p className="activation-eyebrow">Recommended next step</p>
            <h2>Connect an account so your first answer uses your real financial picture.</h2>
            <p>
              Wealth-Y is strongest when it can see balances, spending, debts, and investments.
              Connect one account now, then choose a guided first question.
            </p>
          </div>

          {error && (
            typeof error === 'string'
              ? <div className="activation-error" role="alert">{error}</div>
              : <InlineAlert title={error.title} message={error.message} tone={error.severity || 'error'} />
          )}

          <div className="activation-actions">
            {connectControl || (
              <button
                type="button"
                className="activation-primary"
                data-testid="activation-connect"
                onClick={onConnectPlaid}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect account'}
              </button>
            )}
            <button
              type="button"
              className="activation-secondary"
              data-testid="activation-skip"
              onClick={onSkipPlaid}
            >
              I’ll connect later
            </button>
          </div>
        </div>
      ) : (
        <div className="activation-section">
          <div className="activation-copy">
            <p className="activation-eyebrow">
              {hasPlaidConnection ? 'Ready to ask' : 'Start without accounts'}
            </p>
            <h2>Pick your first question.</h2>
            <p>
              Wealth-Y will use your profile{hasPlaidConnection ? ' and connected accounts' : ''} to answer.
            </p>
          </div>

          {!hasPlaidConnection && (
            <div className="activation-reminder" role="status">
              Connected accounts make answers better. You can still start now.
            </div>
          )}

          {error && (
            typeof error === 'string'
              ? <div className="activation-error" role="alert">{error}</div>
              : <InlineAlert title={error.title} message={error.message} tone={error.severity || 'error'} />
          )}

          <div className="activation-prompts" role="list">
            {promptCards.map((prompt) => (
              <button
                key={prompt.id}
                type="button"
                className="activation-prompt"
                role="listitem"
                data-testid={`activation-prompt-${prompt.id}`}
                onClick={() => onPromptSelected(prompt)}
              >
                <span className="activation-prompt-icon" aria-hidden="true">
                  {React.createElement(PROMPT_ICONS[prompt.icon] || Sparkles, { size: 22 })}
                </span>
                <span className="activation-prompt-copy">
                  <span className="activation-prompt-label">{prompt.label}</span>
                  <span className="activation-prompt-text">{prompt.text}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
